<template>
  <div class="issue-nav header-nav-wrapper">
    <header class="header-nav">
      <!-- Back button -->
      <div class="header-nav__left">
        <router-link
          v-if="displayIssues || useRouterPath"
          class="button button--small"
          :to="navRouterPath"
        >
          <UseSvg
            class="icon"
            name="arrow_left"
          />
        </router-link>
        <a
          v-else
          class="button button--small"
          @click="$router.back()"
        >
          <UseSvg
            class="icon"
            name="arrow_left"
          />
        </a>
      </div>

      <!-- Issue number/total issues -->
      <div
        v-if="issue"
        class="header-nav__center"
      >
        <div class="issue-nav__title-wrapper">
          <div class="issue-nav__count typo-smallish">
            {{ $t('inspections.issue') }} {{ issue.number }} / {{ issue.count }}
          </div>
          <div class="issue-nav__insp-id typo-tiny">
            <router-link
              v-if="!displayIssues"
              :to="inspectionLink"
            >
              {{ capitalize($t('plants.inspection')) }}&nbsp;
              <span>{{ inspectionShortId }}</span>
            </router-link>
            <div v-else>
              {{ capitalize($t('plants.inspection')) }}&nbsp;
              <span>{{ inspectionShortId }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Next/Prev links -->
      <div
        v-if="displayIssues"
        class="header-nav__right"
      >
        <router-link
          :class="previousButtonClass"
          :to="previousIssueRoute"
        >
          <UseSvg
            class="icon"
            name="arrow_left"
          />
        </router-link>

        <router-link
          :class="nextButtonClass"
          :to="nextIssueRoute"
        >
          <UseSvg
            class="icon"
            name="arrow_right"
          />
        </router-link>
      </div>
    </header>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import UseSvg from '@/components/generic/use_svg.vue';

import { capitalize, renderShortId } from '@/functions/utils';
import joinClasses from '@/functions/join_classes';
import getPreviousRoute from '@/functions/get_previous_route';

const props = defineProps({
  issue: { type: Object, default: null },
  inspectionId: { type: String, required: true },
  navRouterPath: { type: Object, required: false, default: null },
  useRouterPath: { type: Boolean, default: false } // prevents page looping from location page to issues page
});

const route = useRoute();

// List of pages that can have the prev/next arrow buttons
const inspectionsList = ['inspection', 'shipInspection', 'shaftInspection', 'shaftV2Inspection', 'equipmentInspection'];

const previousRoute = getPreviousRoute();
const displayIssues = computed(() => {
  if (!props.issue) return false;
  const issuesList = ['issue', 'shaftIssue', 'shaftV2Issue', 'shipIssue', 'equipmentIssue'];
  const pageNameList = [...inspectionsList, ...issuesList];

  const showArrows = pageNameList.some(pageName => {
    return previousRoute?.name === pageName;
  });

  return (!previousRoute || showArrows);
});

const inspectionLink = computed(() => {
  if (!route.name) return;
  let pageName = '';
  switch (true) {
    case route.name.includes('shaftV2'):
      pageName = 'shaftV2Inspection';
      break;

    case route.name.includes('shaft'):
      pageName = 'shaftInspection';
      break;

    case route.name.includes('ship'):
      pageName = 'shipInspection';
      break;

    case route.name.includes('equipment'):
      pageName = 'equipmentInspection';
      break;

    default:
      pageName = 'inspection';
      break;
  }

  return { name: pageName, params: { scope: 'approved_inspections', inspectionId: props.inspectionId } };
});

const previousButtonClass = computed(() => {
  return pagerButtonClass(props.issue.number === 1);
});

const nextButtonClass = computed(() => {
  return pagerButtonClass(props.issue.number === props.issue.count);
});

const inspectionShortId = computed(() => {
  return `#${renderShortId(props.inspectionId)}`;
});

const previousIssueRoute = computed(() => {
  return { params: { issueId: props.issue.previous_issue_id || props.issue.id } };
});

const nextIssueRoute = computed(() => {
  return { params: { issueId: props.issue.next_issue_id || props.issue.id } };
});

function pagerButtonClass(disabled) {
  let modifiers = ['small', 'transparent'];
  if (disabled) modifiers.push('disabled');
  return joinClasses('button', modifiers);
}
</script>
